import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createChart, LineSeries } from 'lightweight-charts';
import { observeChartSize } from '../utils/chartResize';
import { fetchOilSp500 } from '../utils/oilSp500Api';
import {
  fmtSignedCorr,
  fmtOil3mPct,
  oilRegime,
  pickCorr,
  situationAtPoint,
} from '../utils/oilSp500Signal';
import OilSp500Help from './OilSp500Help';
import './BondDashboard.css';
import './OilSp500Panel.css';
import './StockBondReturnsPanel.css';

const RANGE_OPTIONS = ['6M', '1Y', '2Y', '5Y', 'All'];
const DEFAULT_RANGE = '5Y';
const OIL_OPTIONS = [
  { id: 'BZ=F', label: 'Brent (BZ=F)' },
  { id: 'BRNT.MI', label: 'BRNT ETC' },
];
const CORR_MIN = -1;
const CORR_MAX = 1;
const SCALE_MIN_WIDTH = 72;

const COLOR_OIL = '#3D2914';
const COLOR_EQUITY = '#1A4D8C';
const CORR_COLORS = { 30: '#A16D17', 60: '#3A17A1', 90: '#1776A1' };

function toChartData(bars) {
  return (bars || []).map((b) => ({ time: b.time, value: Number(b.value) }));
}

function crosshairTimeString(time) {
  if (time == null) return null;
  if (typeof time === 'string') return time.slice(0, 10);
  if (typeof time === 'object' && time.year != null) {
    const m = String(time.month).padStart(2, '0');
    const d = String(time.day).padStart(2, '0');
    return `${time.year}-${m}-${d}`;
  }
  return String(time).slice(0, 10);
}

function formatChartDate(iso) {
  if (!iso) return '';
  const s = String(iso).slice(0, 10);
  const [y, m, d] = s.split('-');
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[Number(m) - 1]} ${d}, ${y}`;
}

function OilSp500Panel() {
  const chartContainerRef = useRef(null);
  const chartRef = useRef(null);
  const payloadRef = useRef(null);

  const [range, setRange] = useState(DEFAULT_RANGE);
  const [oilSymbol, setOilSymbol] = useState('BZ=F');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [payload, setPayload] = useState(null);
  const [hoverTip, setHoverTip] = useState(null);

  const situation = payload?.situation;
  const latest = payload?.latest;

  useEffect(() => {
    payloadRef.current = payload;
  }, [payload]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchOilSp500({ range, oil: oilSymbol });
      setPayload(data);
    } catch (err) {
      setError(err.message || 'Failed to load Oil vs S&P 500');
      setPayload(null);
    } finally {
      setLoading(false);
    }
  }, [range, oilSymbol]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!payload?.bars || !chartContainerRef.current) return undefined;

    const chart = createChart(chartContainerRef.current, {
      layout: { background: { color: '#FFFBF3' }, textColor: '#16244A' },
      grid: { vertLines: { color: '#EDE3CF' }, horzLines: { color: '#EDE3CF' } },
      crosshair: { mode: 1 },
      leftPriceScale: {
        visible: true,
        borderColor: '#E3D9C6',
        minimumWidth: SCALE_MIN_WIDTH,
        scaleMargins: { top: 0.2, bottom: 0.14 },
      },
      rightPriceScale: {
        visible: true,
        borderColor: '#E3D9C6',
        minimumWidth: SCALE_MIN_WIDTH,
        scaleMargins: { top: 0.12, bottom: 0.12 },
      },
      timeScale: { borderColor: '#E3D9C6', timeVisible: true, secondsVisible: false },
      width: chartContainerRef.current.clientWidth,
      height: chartContainerRef.current.clientHeight || 360,
    });

    const oilSeries = chart.addSeries(LineSeries, {
      color: COLOR_OIL,
      lineWidth: 2,
      priceScaleId: 'left',
      priceLineVisible: false,
      lastValueVisible: true,
      title: 'Oil',
    });
    const eqSeries = chart.addSeries(LineSeries, {
      color: COLOR_EQUITY,
      lineWidth: 2,
      priceScaleId: 'left',
      priceLineVisible: false,
      lastValueVisible: false,
      title: 'S&P',
    });

    const corrSeries = {};
    for (const w of [30, 60, 90]) {
      corrSeries[w] = chart.addSeries(LineSeries, {
        color: CORR_COLORS[w],
        lineWidth: 1.5,
        lineStyle: 1,
        priceScaleId: 'right',
        priceLineVisible: false,
        lastValueVisible: false,
        title: `${w}d`,
      });
    }
    const zeroSeries = chart.addSeries(LineSeries, {
      color: '#4A5672',
      lineWidth: 1,
      lineStyle: 2,
      priceScaleId: 'right',
      priceLineVisible: false,
      lastValueVisible: false,
    });

    const { bars: b } = payload;
    oilSeries.setData(toChartData(b.oil_indexed));
    eqSeries.setData(toChartData(b.equity_indexed));
    corrSeries[30].setData(toChartData(b.corr30));
    corrSeries[60].setData(toChartData(b.corr60));
    corrSeries[90].setData(toChartData(b.corr90));
    const zeroBase = b.corr90?.length ? b.corr90 : b.corr30;
    if (zeroBase?.length) {
      zeroSeries.setData(zeroBase.map((p) => ({ time: p.time, value: 0 })));
    }

    try {
      chart.priceScale('right').applyOptions({ autoScale: false, scaleMargins: { top: 0.08, bottom: 0.08 } });
      chart.priceScale('right').setVisibleRange({ from: CORR_MIN, to: CORR_MAX });
    } catch {
      /* empty */
    }
    chart.timeScale().fitContent();

    const onCrosshairMove = (param) => {
      if (!param.time || param.point == null) {
        setHoverTip(null);
        return;
      }
      const time = crosshairTimeString(param.time);
      const p = payloadRef.current?.bars;
      if (!p) {
        setHoverTip(null);
        return;
      }
      const c30 = pickCorr(p.corr30, time);
      const c60 = pickCorr(p.corr60, time);
      const c90 = pickCorr(p.corr90, time);
      const oil3m = pickCorr(p.oil_3m_log_return, time);
      if (c30 == null && c90 == null) {
        setHoverTip(null);
        return;
      }
      setHoverTip({
        x: param.point.x,
        y: param.point.y,
        time,
        ...situationAtPoint(c30, c60, c90, oil3m),
      });
    };
    chart.subscribeCrosshairMove(onCrosshairMove);
    chartRef.current = chart;

    const disconnect = observeChartSize(chartContainerRef.current, (width, height) => {
      chart.applyOptions({ width, height: height || 360 });
    });

    return () => {
      chart.unsubscribeCrosshairMove(onCrosshairMove);
      disconnect();
      chart.remove();
      chartRef.current = null;
      setHoverTip(null);
    };
  }, [payload]);

  const r90 = useMemo(
    () => oilRegime(latest?.correlation?.d90, latest?.oil_3m_log_return),
    [latest],
  );

  return (
    <section id="macro-oil-sp500" className="bond-dash-panel oil-sp500-panel">
      <div className="bond-dash-header oil-sp500-header">
        <div>
          <h3 className="bond-dash-title">Oil (Brent) vs S&P 500 — demand or supply?</h3>
          <p className="bond-dash-subtitle">
            {payload?.oil_symbol} vs {payload?.equity_symbol || '^GSPC'}
            {payload?.oil_is_etp ? ' · ETC (roll + currency caveat)' : ''}
            {payload?.diagnostics?.aligned_from && payload?.diagnostics?.aligned_to
              ? ` · ${payload.diagnostics.aligned_from} → ${payload.diagnostics.aligned_to}`
              : ''}
            . Hover for correlation and 3-month oil trend at that date.
          </p>
          <OilSp500Help />
        </div>
        {latest && (
          <div className="btc-nasdaq-legend">
            <span className="oil-sp500-legend-oil">
              Oil {latest.oil_indexed?.toFixed(1) ?? '—'}
            </span>
            <span className="oil-sp500-legend-equity">
              S&P {latest.equity_indexed?.toFixed(1) ?? '—'}
            </span>
            <span className={`oil-sp500-regime oil-sp500-regime--${r90.cssClass}`}>
              {r90.emoji} {r90.label} (90d · oil 3m {fmtOil3mPct(latest.oil_3m_log_return)})
            </span>
          </div>
        )}
      </div>

      {situation && (
        <div className="btc-nasdaq-signal-block">
          <span
            className={`btc-nasdaq-signal-headline oil-sp500-regime--${situation.primary_css_class}`}
          >
            {situation.headline}
          </span>
          <p className="btc-nasdaq-signal-detail">{situation.detail}</p>
          <p className="btc-nasdaq-signal-tooltip-line">{situation.tooltip_line}</p>
        </div>
      )}

      <div className="stock-bond-toolbar">
        <span className="stock-bond-range-label">Range</span>
        {RANGE_OPTIONS.map((label) => (
          <button
            key={label}
            type="button"
            className={`stock-bond-range-btn${range === label ? ' active' : ''}`}
            onClick={() => setRange(label)}
            disabled={loading}
          >
            {label}
          </button>
        ))}
        <span className="stock-bond-range-label">Oil leg</span>
        {OIL_OPTIONS.map(({ id, label }) => (
          <button
            key={id}
            type="button"
            className={`stock-bond-range-btn${oilSymbol === id ? ' active' : ''}`}
            onClick={() => setOilSymbol(id)}
            disabled={loading}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="stock-bond-charts">
        <div className="stock-bond-corr-header">
          <span className="stock-bond-corr-title">Rolling correlation (daily log returns)</span>
          {latest?.correlation && (
            <div className="stock-bond-corr-legend">
              <span style={{ color: CORR_COLORS[30] }}>30d {fmtSignedCorr(latest.correlation.d30)}</span>
              <span style={{ color: CORR_COLORS[60] }}>60d {fmtSignedCorr(latest.correlation.d60)}</span>
              <span style={{ color: CORR_COLORS[90] }}>90d {fmtSignedCorr(latest.correlation.d90)}</span>
            </div>
          )}
        </div>

        {loading && (
          <div className="oil-sp500-chart-placeholder">Loading Oil vs S&P 500…</div>
        )}

        {!loading && error && (
          <div className="oil-sp500-chart-placeholder oil-sp500-chart-placeholder--error">
            <div>
              <strong>{error}</strong>
              <p style={{ marginTop: 8, color: 'var(--ink-2)' }}>
                Backfill Brent and S&P data, then restart the investing API (port 5001):
                <br />
                <code>python3 -m investing.backend --benchmarks-only</code>
              </p>
            </div>
          </div>
        )}

        {!loading && !error && payload?.bars && (
          <div className="stock-bond-chart-wrap">
            <span className="stock-bond-axis-label stock-bond-axis-label--left">Indexed (100)</span>
            <div ref={chartContainerRef} className="stock-bond-chart stock-bond-chart-combined" />
            {hoverTip && (
              <div
                className="stock-bond-chart-tooltip"
                style={{ left: hoverTip.x + 14, top: hoverTip.y + 14 }}
                role="tooltip"
              >
                <div className="stock-bond-chart-tooltip-date">{formatChartDate(hoverTip.time)}</div>
                <div className={`stock-bond-chart-tooltip-regime oil-sp500-regime--${hoverTip.primaryCss}`}>
                  {hoverTip.reg?.emoji} {hoverTip.headline}
                </div>
                <p className="stock-bond-chart-tooltip-body">{hoverTip.detail}</p>
              </div>
            )}
            <span className="stock-bond-axis-label stock-bond-axis-label--right">Correlation</span>
          </div>
        )}
      </div>
    </section>
  );
}

export default OilSp500Panel;
